import { findAll, slugify } from './dom.js';
import { compareFacetValues, queryToSelection, selectionToQuery } from './product-filter.js';

const CAPABILITY_SELECTOR = '[data-bp-insights="v2"]';
const READY_ATTRIBUTE = 'data-bp-insights-ready';
const GROUPS = ['market', 'topic', 'type'];
const GROUP_LABELS = { market: 'Markets', topic: 'Topics', type: 'Types' };
// Articles filed under this pillar are the market articles; every other pillar is a topic group.
const MARKET_PILLAR = 'Market Insights';
// The cards a first view shows per grid column count: the lead card plus the rows the design draws.
const FIRST_VIEW = {
  latest: { 1: 5, 2: 9, 3: 11, 4: 11 },
  filtered: { 1: 5, 2: 7, 3: 10, 4: 13 },
};
const SEARCH_DELAY = 150;

export function readInsight(item) {
  const facet = (name) => (item.querySelector(`[data-bp-facets] [data-f="${name}"]`)?.textContent ?? '').trim();
  const title = (item.querySelector('.ab-mcard-title')?.textContent ?? '').trim();
  const marker = item.querySelector('[data-bp-featured]');
  return {
    pillar: facet('pillar'),
    topic: facet('topic'),
    type: facet('type'),
    text: `${title} ${facet('excerpt')}`.toLowerCase(),
    featured: Boolean(marker && !marker.classList.contains('w-condition-invisible')),
  };
}

// Markets and types are flat lists; topics are grouped under their pillar. Everything comes from the articles, so a new
// topic or type shows up as soon as an article uses it.
export function buildOptions(insights) {
  const markets = new Set();
  const types = new Set();
  const pillars = new Map();
  for (const insight of insights) {
    if (insight.type) types.add(insight.type);
    if (!insight.topic) continue;
    if (insight.pillar === MARKET_PILLAR) {
      markets.add(insight.topic);
      continue;
    }
    const pillar = insight.pillar || 'Other';
    if (!pillars.has(pillar)) pillars.set(pillar, new Set());
    pillars.get(pillar).add(insight.topic);
  }
  return {
    market: [...markets].sort(compareFacetValues),
    topic: [...pillars.entries()]
      .sort(([a], [b]) => compareFacetValues(a, b))
      .map(([pillar, topics]) => ({ pillar, topics: [...topics].sort(compareFacetValues) })),
    type: [...types].sort(compareFacetValues),
  };
}

export function flatOptions(options) {
  return {
    market: options.market,
    topic: options.topic.flatMap((group) => group.topics),
    type: options.type,
  };
}

// `?pillar=<slug>` (the header's Insights tiles) selects every topic filed under that pillar; Market Insights means every market.
export function pillarSelection(options, pillar) {
  const wanted = slugify(pillar ?? '');
  if (!wanted) return {};
  if (wanted === slugify(MARKET_PILLAR)) return { market: [...options.market] };
  const group = options.topic.find((entry) => slugify(entry.pillar) === wanted);
  return group ? { topic: [...group.topics] } : {};
}

// Markets and topics both read the article's one primary topic, so they widen each other (OR); types and the search
// words narrow (AND).
export function matchInsights(insights, selection, query = '') {
  const topics = [...(selection.market ?? []), ...(selection.topic ?? [])];
  const types = selection.type ?? [];
  const words = query.toLowerCase().split(/\s+/u).filter(Boolean);
  return insights.map((insight) => (
    (topics.length === 0 || topics.includes(insight.topic)) &&
    (types.length === 0 || types.includes(insight.type)) &&
    words.every((word) => insight.text.includes(word) || insight.topic.toLowerCase().includes(word))
  ));
}

// The lead card is the newest article marked Featured, or the newest article when none is.
export function featuredIndex(insights) {
  const index = insights.findIndex((insight) => insight.featured);
  return index === -1 ? 0 : index;
}

export function countSelected(selection) {
  return GROUPS.reduce((sum, group) => sum + (selection[group]?.length ?? 0), 0);
}

function columnCount(grid, windowObject) {
  const columns = windowObject.getComputedStyle(grid).gridTemplateColumns;
  return Math.max(1, Math.min(4, columns.split(' ').filter(Boolean).length));
}

function createElement(documentObject, tag, attributes = {}, text) {
  const element = documentObject.createElement(tag);
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  if (text !== undefined) element.textContent = text;
  return element;
}

function createOption(documentObject, group, value, label = value) {
  const option = createElement(documentObject, 'label', { class: 'ins-opt' });
  const input = createElement(documentObject, 'input', { type: 'checkbox', name: group, value });
  option.append(input, createElement(documentObject, 'span', {}, label));
  return option;
}

// Webflow paginates a list past 100 items; the later pages are fetched and their items appended to this grid.
async function loadRemainingPages(section, grid, fetchPage, documentObject) {
  const added = [];
  let next = section.querySelector('.w-pagination-next');
  const seen = new Set();
  while (next?.getAttribute('href') && !seen.has(next.getAttribute('href'))) {
    const href = next.getAttribute('href');
    seen.add(href);
    const html = await fetchPage(new URL(href, documentObject.baseURI).href);
    const page = new documentObject.defaultView.DOMParser().parseFromString(html, 'text/html');
    const pageSection = page.querySelector(CAPABILITY_SELECTOR);
    for (const item of pageSection?.querySelectorAll('.w-dyn-items > .w-dyn-item') ?? []) {
      const imported = documentObject.importNode(item, true);
      grid.append(imported);
      added.push(imported);
    }
    next = pageSection?.querySelector('.w-pagination-next') ?? null;
  }
  return added;
}

function initializeInsights(section, environment) {
  const grid = section.querySelector('.w-dyn-items');
  const bar = section.querySelector('[data-bp-insights-bar]');
  if (!grid || !bar || section.hasAttribute(READY_ATTRIBUTE)) return false;

  const documentObject = environment.document ?? section.ownerDocument;
  const windowObject = environment.window ?? documentObject.defaultView;
  const historyObject = environment.history ?? windowObject.history;
  const locationObject = environment.location ?? windowObject.location;
  const fetchPage = environment.fetchPage ?? ((url) => windowObject.fetch(url, { credentials: 'same-origin' }).then((response) => {
    if (!response.ok) throw new Error(`Insights page ${response.status}`);
    return response.text();
  }));

  let items = Array.from(grid.children).filter((item) => item.classList.contains('w-dyn-item'));
  let insights = items.map(readInsight);
  let options = buildOptions(insights);
  const selectionFromUrl = () => ({
    ...queryToSelection(locationObject.search, flatOptions(options)),
    ...pillarSelection(options, new URLSearchParams(locationObject.search).get('pillar')),
  });
  let selection = selectionFromUrl();
  let query = new URLSearchParams(locationObject.search).get('q') ?? '';
  let expanded = false;
  let columns = columnCount(grid, windowObject);

  section.querySelector('.w-pagination-wrapper')?.setAttribute('hidden', '');

  // Search: the field lives in the bar's search slot; the icon is the slot's own background.
  const searchSlot = bar.querySelector('[data-bp-insights-search]');
  const search = createElement(documentObject, 'input', {
    type: 'search',
    class: 'ins-search-input',
    placeholder: 'Search...',
    'aria-label': 'Search insights',
    autocomplete: 'off',
  });
  search.value = query;
  searchSlot?.append(search);

  const status = createElement(documentObject, 'p', { class: 'ins-status', role: 'status' });
  const empty = createElement(documentObject, 'p', { class: 'ins-empty', hidden: '' }, 'No insights match these filters.');
  grid.closest('.w-dyn-list')?.after(empty);
  bar.append(status);

  const moreSlot = section.querySelector('[data-bp-insights-more]');
  const more = moreSlot?.querySelector('a, button');
  more?.setAttribute('role', 'button');

  const toggles = Object.fromEntries(GROUPS.map((group) => [group, bar.querySelector(`[data-bp-insights-toggle="${group}"]`)]));
  const panels = {};

  const buildPanel = (group) => {
    const panel = createElement(documentObject, 'div', {
      class: 'ins-panel',
      id: `ins-panel-${group}`,
      role: 'group',
      'aria-label': GROUP_LABELS[group],
      'data-bp-insights-panel': group,
      hidden: '',
    });
    const body = createElement(documentObject, 'div', { class: 'ins-panel-opts' });
    if (group === 'topic') {
      for (const { pillar, topics } of options.topic) {
        const titleId = `ins-group-${slugify(pillar)}`;
        const fieldset = createElement(documentObject, 'div', { class: 'ins-group', role: 'group', 'aria-labelledby': titleId });
        fieldset.append(createElement(documentObject, 'p', { class: 'ins-group-title', id: titleId }, pillar));
        const all = createOption(documentObject, '', pillar, 'All');
        all.querySelector('input').setAttribute('data-bp-insights-all', '');
        all.querySelector('input').removeAttribute('name');
        fieldset.append(all);
        for (const topic of topics) fieldset.append(createOption(documentObject, group, topic));
        body.append(fieldset);
      }
    } else {
      for (const value of options[group]) body.append(createOption(documentObject, group, value));
    }
    const foot = createElement(documentObject, 'div', { class: 'ins-panel-foot' });
    const apply = createElement(documentObject, 'button', { type: 'button', class: 'ins-apply' }, 'View Results');
    const reset = createElement(documentObject, 'button', { type: 'button', class: 'ins-reset' }, 'Clear All');
    foot.append(apply, reset);
    panel.append(body, foot);
    apply.addEventListener('click', () => {
      selection = { ...selection, [group]: readPanel(group) };
      commit();
      close(true);
    });
    reset.addEventListener('click', () => {
      selection = { ...selection, [group]: [] };
      commit();
      close(true);
    });
    panel.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof windowObject.HTMLInputElement)) return;
      const fieldset = input.closest('.ins-group');
      if (!fieldset) return;
      const topicInputs = Array.from(fieldset.querySelectorAll('input[name="topic"]'));
      if (input.hasAttribute('data-bp-insights-all')) {
        for (const topicInput of topicInputs) topicInput.checked = input.checked;
      }
      syncAll(fieldset);
    });
    return panel;
  };

  const syncAll = (fieldset) => {
    const all = fieldset.querySelector('[data-bp-insights-all]');
    const topicInputs = Array.from(fieldset.querySelectorAll('input[name="topic"]'));
    const checked = topicInputs.filter((input) => input.checked).length;
    all.checked = checked > 0 && checked === topicInputs.length;
    all.indeterminate = checked > 0 && checked < topicInputs.length;
  };

  const readPanel = (group) => Array.from(panels[group].querySelectorAll(`input[name="${group}"]`))
    .filter((input) => input.checked)
    .map((input) => input.value);

  const writePanel = (group) => {
    for (const input of panels[group].querySelectorAll(`input[name="${group}"]`)) {
      input.checked = (selection[group] ?? []).includes(input.value);
    }
    for (const fieldset of panels[group].querySelectorAll('.ins-group')) syncAll(fieldset);
  };

  const buildPanels = () => {
    for (const group of GROUPS) {
      panels[group]?.remove();
      const toggle = toggles[group];
      if (!toggle) continue;
      panels[group] = buildPanel(group);
      bar.append(panels[group]);
      toggle.setAttribute('aria-controls', panels[group].id);
    }
  };

  const openGroup = () => GROUPS.find((group) => toggles[group]?.getAttribute('aria-expanded') === 'true');

  const close = (refocus) => {
    const group = openGroup();
    if (!group) return;
    panels[group].hidden = true;
    toggles[group].setAttribute('aria-expanded', 'false');
    bar.removeAttribute('data-bp-insights-open');
    if (refocus) toggles[group].focus();
  };

  const open = (group) => {
    close(false);
    writePanel(group);
    panels[group].hidden = false;
    toggles[group].setAttribute('aria-expanded', 'true');
    bar.setAttribute('data-bp-insights-open', group);
    panels[group].querySelector('input')?.focus({ preventScroll: true });
  };

  const updateToggles = () => {
    for (const group of GROUPS) {
      const toggle = toggles[group];
      if (!toggle) continue;
      const [label, value] = toggle.querySelectorAll('span');
      const chosen = selection[group] ?? [];
      if (label) label.textContent = chosen.length ? `${GROUP_LABELS[group]} (${chosen.length})` : GROUP_LABELS[group];
      if (value) value.textContent = chosen.length ? chosen.join(', ') : `All ${GROUP_LABELS[group]}`;
      toggle.toggleAttribute('data-bp-insights-active', chosen.length > 0);
    }
  };

  const isFiltered = () => countSelected(selection) > 0 || query.trim() !== '';

  const render = (announce) => {
    const filtered = isFiltered();
    const matches = matchInsights(insights, selection, query);
    const total = matches.filter(Boolean).length;
    const limit = expanded ? Infinity : FIRST_VIEW[filtered ? 'filtered' : 'latest'][columns];
    let shown = 0;
    items.forEach((item, index) => {
      const visible = matches[index] && shown < limit;
      item.hidden = !visible;
      item.toggleAttribute('data-bp-lead', visible && shown === 0);
      if (visible) shown += 1;
    });
    section.toggleAttribute('data-bp-filtered', filtered);
    empty.hidden = total > 0;
    if (moreSlot) moreSlot.hidden = shown >= total;
    if (announce) status.textContent = `Showing ${shown} of ${total} insights`;
    updateToggles();
  };

  const commit = () => {
    expanded = false;
    render(true);
    const params = selectionToQuery(selection, GROUPS, locationObject.search).replace(/^\?/u, '');
    const next = new URLSearchParams(params);
    next.delete('pillar');
    if (query.trim()) next.set('q', query.trim());
    else next.delete('q');
    const search = next.toString().replace(/%2C/gu, ',');
    historyObject.replaceState(historyObject.state, '', `${locationObject.pathname}${search ? `?${search}` : ''}${locationObject.hash}`);
  };

  const placeFeatured = () => {
    const index = featuredIndex(insights);
    if (index === 0) return;
    const [item] = items.splice(index, 1);
    const [insight] = insights.splice(index, 1);
    items.unshift(item);
    insights.unshift(insight);
    grid.prepend(item);
  };

  for (const group of GROUPS) {
    toggles[group]?.setAttribute('type', 'button');
    toggles[group]?.addEventListener('click', () => {
      if (openGroup() === group) close(true);
      else open(group);
    });
  }
  const clear = bar.querySelector('[data-bp-insights-clear]');
  clear?.setAttribute('type', 'button');
  clear?.addEventListener('click', () => {
    selection = { market: [], topic: [], type: [] };
    query = '';
    search.value = '';
    close(false);
    commit();
  });

  let searchTimer;
  search.addEventListener('input', () => {
    windowObject.clearTimeout(searchTimer);
    searchTimer = windowObject.setTimeout(() => {
      query = search.value;
      commit();
    }, SEARCH_DELAY);
  });

  documentObject.addEventListener('pointerdown', (event) => {
    if (openGroup() && !bar.contains(event.target)) close(false);
  });
  // Tabbing out of the bar closes the panel; a click on the panel's own background moves focus nowhere and keeps it.
  bar.addEventListener('focusout', (event) => {
    if (openGroup() && event.relatedTarget && !bar.contains(event.relatedTarget)) close(false);
  });
  documentObject.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openGroup()) {
      event.preventDefault();
      close(true);
    }
  });

  more?.addEventListener('click', (event) => {
    event.preventDefault();
    // Webflow's delegated smooth scroll ignores preventDefault and would jump to the button's in-page anchor.
    event.stopPropagation();
    const before = items.filter((item) => !item.hidden);
    expanded = true;
    render(true);
    const next = items.find((item) => !item.hidden && !before.includes(item));
    if (!next) return;
    next.setAttribute('tabindex', '-1');
    next.focus({ preventScroll: false });
  });
  more?.addEventListener('keydown', (event) => {
    if (event.key === ' ') {
      event.preventDefault();
      more.click();
    }
  });

  windowObject.addEventListener('resize', () => {
    const next = columnCount(grid, windowObject);
    if (next === columns) return;
    columns = next;
    render(false);
  });

  placeFeatured();
  buildPanels();
  render(false);
  section.setAttribute(READY_ATTRIBUTE, '');

  loadRemainingPages(section, grid, fetchPage, documentObject).then((added) => {
    if (added.length === 0) return;
    items = items.concat(added);
    insights = insights.concat(added.map(readInsight));
    placeFeatured();
    options = buildOptions(insights);
    selection = selectionFromUrl();
    buildPanels();
    render(false);
  }).catch((error) => {
    windowObject.console?.error('[berlin-site] insights', error);
  });
  return true;
}

export function initializeInsightsFilter(root, environment = {}) {
  let initialized = 0;
  for (const section of findAll(root, CAPABILITY_SELECTOR)) {
    if (initializeInsights(section, environment)) initialized += 1;
  }
  return initialized;
}
